// More widgets for custom dashboards: today's rules from Morning prep (with a streak), MARK's
// coaching focus, open positions from Interactive Brokers and Schwab, and P&L by weekday and by symbol.
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from './api';
import { currentTradingDay, netPnl, tradingDayKey, usd } from './pnl';
import { useI18n } from './i18n';
import { useOpenPositions } from './OpenPositions';

const tone = (n) => (n > 0 ? 'gain' : n < 0 ? 'loss' : '');
const signed = (n) => `${n > 0 ? '+' : n < 0 ? '−' : ''}${usd.format(Math.abs(n || 0))}`;

// Loads once; { data, error } with data undefined while loading.
function useLoad(path) {
  const [state, setState] = useState({ data: undefined, error: null });
  useEffect(() => {
    let live = true;
    api(path).then((data) => live && setState({ data, error: null })).catch((error) => live && setState({ data: null, error }));
    return () => { live = false; };
  }, [path]);
  return state;
}

function Card({ title, children, className = '' }) {
  const { t } = useI18n();
  return (
    <section className={`dash-card dw-card pw-card ${className}`}>
      <h3 className="dw-title">{t(title)}</h3>
      {children}
    </section>
  );
}

// --- Today's rules ---------------------------------------------------------------------------

// The rules from Morning prep, ticked for today, and how many trading days in a row every rule held.
export function RulesToday({ notes = {} }) {
  const { t } = useI18n();
  const navigate = useNavigate();
  const { data } = useLoad('/routine');
  const rules = (data && data.rules) || [];
  const today = currentTradingDay();
  const kept = (notes[today] && notes[today].rulesFollowed) || [];
  // The streak counts days with a check-in, newest first, until one where a rule was missed.
  let streak = 0;
  for (const key of Object.keys(notes).filter((k) => k <= today).sort().reverse()) {
    const followed = notes[key] && notes[key].rulesFollowed;
    if (!followed || !followed.length) continue;
    if (rules.length && rules.every((r) => followed.includes(r))) streak += 1;
    else break;
  }
  return (
    <Card title="Rules followed today" className="mw-rules">
      {data === undefined ? <span className="dw-skeleton" /> : !rules.length ? (
        <div className="pw-empty">
          <p>{t('Write your trading rules in Morning prep, then tick them off each day.')}</p>
          <button type="button" className="secondary small" onClick={() => navigate('/app/morning')}>{t('Open Morning prep')}</button>
        </div>
      ) : (
        <>
          <span className="pw-head"><span className="pw-big">{kept.filter((r) => rules.includes(r)).length} / {rules.length}</span>{streak > 0 && <span className="mw-streak">🔥 {t(streak === 1 ? '{n} day in a row' : '{n} days in a row', { n: streak })}</span>}</span>
          <ul className="mw-checks">
            {rules.slice(0, 5).map((r) => <li key={r} className={kept.includes(r) ? 'on' : ''}><span aria-hidden="true">{kept.includes(r) ? '✓' : ''}</span>{r}</li>)}
          </ul>
          <button type="button" className="link-button" onClick={() => navigate('/app/morning')}>{t('Tick today’s rules')} &rarr;</button>
        </>
      )}
    </Card>
  );
}

// --- MARK's coaching focus -------------------------------------------------------------------

export function MarkFocus() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const { data, error } = useLoad('/mark/focus');
  const plan = data && data.plan;
  const today = currentTradingDay();
  const from = new Date(`${today}T12:00:00Z`);
  from.setUTCDate(from.getUTCDate() - 6);
  const week = plan ? (plan.checks || []).filter((c) => c.day >= from.toISOString().slice(0, 10) && (c.held === true || c.held === false)) : [];
  const held = week.filter((c) => c.held).length;
  return (
    <Card title="MARK’s coaching focus" className="mw-mark">
      {data === undefined && !error ? <span className="dw-skeleton" /> : error ? (
        <div className="pw-empty"><p>{t('Coach MARK isn’t on for your account yet.')}</p></div>
      ) : !plan ? (
        <div className="pw-empty">
          <p>{t('Ask MARK for a review and he’ll set the one thing to work on, with a rule to follow.')}</p>
          <button type="button" className="secondary small" onClick={() => navigate('/app/mark')}>{t('Open Coach MARK')}</button>
        </div>
      ) : (
        <>
          <p className="mw-job">{plan.job}</p>
          {plan.rule && <p className="mw-rule">{plan.rule}</p>}
          <small className="dov-muted">{week.length ? t('Rule followed: {held} of {n} sessions this week', { held, n: week.length }) : t('Mark each session in your Action plan to see how the rule holds.')}</small>
          <button type="button" className="link-button" onClick={() => navigate('/app/mark')}>{t('Open Coach MARK')} &rarr;</button>
        </>
      )}
    </Card>
  );
}

// --- Open positions --------------------------------------------------------------------------

export function OpenPositionsWidget({ accounts = [] }) {
  const { t } = useI18n();
  const navigate = useNavigate();
  const loaded = useOpenPositions(false);
  const data = loaded === null ? undefined : loaded;
  const positions = (data && data.positions) || [];
  const nameOf = (id) => { const a = accounts.find((x) => String(x._id) === String(id)); return a ? a.name : ''; };
  const total = positions.reduce((s, p) => s + (Number(p.unrealized) || 0), 0);
  return (
    <Card title="Open positions" className="mw-positions">
      {data === undefined ? <span className="dw-skeleton" /> : !positions.length ? (
        <div className="pw-empty">
          <p>{t('No open positions. Swing trades synced from your broker show here while they’re open.')}</p>
          <button type="button" className="secondary small" onClick={() => navigate('/app/accounts')}>{t('Set up accounts')}</button>
        </div>
      ) : (
        <>
          <span className="pw-head"><span className="dov-muted">{t(positions.length === 1 ? '{n} open position' : '{n} open positions', { n: positions.length })}</span><span className={`pw-big ${tone(total)}`}>{signed(total)}</span></span>
          <ul className="mw-rows">
            {positions.slice(0, 6).map((p, i) => (
              <li key={`${p.symbol}${i}`}>
                <span><b>{p.symbol}</b> <span className={`dov-side ${String(p.side).toLowerCase().startsWith('s') ? 'short' : 'long'}`}>{t(p.side)} ×{p.size}</span><small className="dov-muted">@ {p.entryPrice}{nameOf(p.accountId) ? ` · ${nameOf(p.accountId)}` : ''}</small></span>
                <b className={tone(Number(p.unrealized) || 0)}>{p.markPrice ? signed(Number(p.unrealized) || 0) : '—'}</b>
              </li>
            ))}
          </ul>
        </>
      )}
    </Card>
  );
}

// --- P&L by time of day, weekday and symbol ---------------------------------------------------

// Rows with a bar that grows left (loss) or right (gain) from the middle.
function SplitBars({ rows, empty }) {
  const { t } = useI18n();
  if (!rows.length) return <p className="dov-muted">{t(empty)}</p>;
  const max = Math.max(1, ...rows.map((r) => Math.abs(r.net)));
  return (
    <ul className="mw-split">
      {rows.map((r) => (
        <li key={r.key} title={`${r.label}: ${signed(r.net)} · ${t(r.count === 1 ? '{n} trade' : '{n} trades', { n: r.count })}`}>
          <span className="mw-split-label">{r.label}</span>
          <span className="mw-split-track"><i className={tone(r.net)} style={{ width: `${(Math.abs(r.net) / max) * 50}%`, [r.net < 0 ? 'right' : 'left']: '50%' }} /></span>
          <b className={tone(r.net)}>{signed(r.net)}</b>
        </li>
      ))}
    </ul>
  );
}

function group(trades, keyOf) {
  const out = new Map();
  for (const x of trades) {
    const key = keyOf(x);
    if (key === null) continue;
    const g = out.get(key) || { key, net: 0, count: 0 };
    g.net += netPnl(x);
    g.count += 1;
    out.set(key, g);
  }
  return [...out.values()];
}


const WEEKDAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
export function PnlByWeekday({ trades }) {
  const { t } = useI18n();
  const rows = group(trades, (x) => new Date(`${tradingDayKey(x.date)}T12:00:00Z`).getUTCDay())
    .sort((a, b) => ((a.key + 6) % 7) - ((b.key + 6) % 7)).map((g) => ({ ...g, label: t(WEEKDAYS[g.key]) }));
  return <Card title="P&L by weekday" className="mw-by"><SplitBars rows={rows} empty="No trades yet." /></Card>;
}

// Futures contracts by their root (ESZ6 and ESH7 are both ES); everything else by its symbol.
export const symbolRoot = (symbol) => {
  const s = String(symbol || '').toUpperCase().trim();
  const m = s.match(/^([A-Z0-9]{1,4}?)[FGHJKMNQUVXZ]\d{1,2}$/);
  return m && m[1].length >= 1 ? m[1] : s;
};

export function PnlBySymbol({ trades }) {
  const rows = group(trades, (x) => (x.symbol ? symbolRoot(x.symbol) : null))
    .sort((a, b) => Math.abs(b.net) - Math.abs(a.net)).slice(0, 8).sort((a, b) => b.net - a.net).map((g) => ({ ...g, label: g.key }));
  return <Card title="P&L by symbol" className="mw-by"><SplitBars rows={rows} empty="No trades yet." /></Card>;
}
